import React from "react";
import {useEffect, useState, useCallback} from "react";
import {API_URL} from "../../services/api.service";
import axios from "axios";
import useSWR from "swr";
import {useAuthContext} from "../../services/auth.service";
import {useVirtual} from "react-virtual";
import AutoSizer from "react-virtualized-auto-sizer";
import Button from "../_core/Button";
import {useDataContext} from "../../services/data.service";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUp, faArrowDown, faSearchMinus} from "@fortawesome/free-solid-svg-icons";


const ImagesList = ({ currentPage, currentImageID, searchParams, setSearchParams, setCurrentImage }) => {

    const {token} = useAuthContext();
    const [dataCount, setDataCount] = useState(0);

    const {
        imageStatuses
    } = useDataContext();

    const currentSortBy = searchParams.get("sort_by") || "id";
    const currentSortOrder = searchParams.get("sort_order") || "asc";
    const currentSearch = searchParams.get("search") || "";

    const handleSort = useCallback((column) => {
        const newParams = new URLSearchParams(searchParams);
        if (currentSortBy === column) {
            // Toggle order if same column
            newParams.set("sort_order", currentSortOrder === "asc" ? "desc" : "asc");
        } else {
            // New column, default to ascending
            newParams.set("sort_by", column);
            newParams.set("sort_order", "asc");
        }
        setSearchParams(newParams);
    }, [currentSortBy, currentSortOrder, searchParams, setSearchParams]);

    const SortIndicator = ({ column }) => {
        if (currentSortBy !== column) return null;
        return (
            <FontAwesomeIcon
                icon={currentSortOrder === "asc" ? faArrowUp : faArrowDown}
                className="ml-1 text-xs"
            />
        );
    };

    const SortableHeader = ({ column, children, className }) => (
        <h3
            className={`${className} cursor-pointer hover:text-blue-600 select-none transition-colors duration-150`}
            onClick={() => handleSort(column)}
        >
            {children}
            <SortIndicator column={column} />
        </h3>
    );

    const datasetParams = new URLSearchParams(searchParams);
    datasetParams.set("page", currentPage.toString());
    const DATASET_IMAGES_URL = `${API_URL}/images?${datasetParams.toString()}`
    const fetchDatasetImages = async () =>
        await axios.get(DATASET_IMAGES_URL, {
            headers: {
                "Authorization": `Bearer ${token}`
            }
        })
            .then(result => result.data)
            .catch(error => console.log("error", error));

    const {
        data: datasetImages,
        error: datasetImagesError
    } = useSWR(DATASET_IMAGES_URL, fetchDatasetImages);
    if (datasetImagesError) console.error(datasetImagesError);

    const totalResults = datasetImages?.totalResults || 0;

    useEffect(() => {
        if (datasetImages && datasetImages.resultCount) {
            setDataCount(datasetImages.resultCount)
        } else if (datasetImages && datasetImages.resultCount === 0) {
            setDataCount(0)
        }
    }, [datasetImages])

    const parentRef = React.useRef();

    const rowVirtualizer = useVirtual({
        size: dataCount,
        parentRef,
        estimateSize: React.useCallback(() => 45, []),
        overscan: 3
    });

    const rowBG = (rowIndex) => {
        let matchingIndex = -1;
        if (currentImageID)
            matchingIndex = datasetImages.results.findIndex(x => x.id === currentImageID);

        if (matchingIndex !== -1 && matchingIndex === rowIndex)
            return "bg-blue-100 text-blue-700 hover:bg-blue-200"

        return "bg-white hover:bg-gray-200"
    }

    const ready = !!datasetImages && dataCount !== 0;

    return (
        <div className="bg-slate-200 absolute inset-0 z-50 flex flex-col">
            {/* Result count bar */}
            {datasetImages && (
                <div className="w-full bg-slate-100 px-5 py-2 text-sm text-slate-600 border-b border-slate-300">
                    {currentSearch ? (
                        <span>
                            <span className="font-medium">{totalResults.toLocaleString()}</span> {totalResults === 1 ? 'result' : 'results'} for "<span className="font-medium">{currentSearch}</span>"
                        </span>
                    ) : (
                        <span><span className="font-medium">{totalResults.toLocaleString()}</span> {totalResults === 1 ? 'image' : 'images'}</span>
                    )}
                </div>
            )}
            {/* Column headers */}
            <div className="w-full bg-white flex justify-between px-5 py-3 font-bold text-sm uppercase tracking-wider text-slate-500">
                <SortableHeader column="dataset_id" className="w-40">Dataset</SortableHeader>
                <SortableHeader column="sequence" className="w-24">Sequence</SortableHeader>
                <SortableHeader column="name" className="w-96">Name</SortableHeader>
                <SortableHeader column="status" className="w-36">Status</SortableHeader>
                <h3 className="w-32 text-right"></h3>
            </div>
            <div className="w-full overflow-y-auto overflow-x-hidden flex-grow" ref={parentRef}>
            {!ready ? (
                <div className="w-full h-full pt-36 text-center text-gray-500">
                    {datasetImages && datasetImages.resultCount === 0 ? (
                        <div className="flex flex-col items-center">
                            <FontAwesomeIcon icon={faSearchMinus} className="text-5xl mb-4 text-gray-300" />
                            <p className="text-xl font-medium mb-2">No images found</p>
                            {currentSearch && <p className="text-sm">Try a different search term</p>}
                        </div>
                    ) : (
                        <p className="text-lg">Loading images...</p>
                    )}
                </div>
            ) :
                <AutoSizer>
                    {({width, height}) => (
                        <div className={(rowVirtualizer.totalSize + 45) > height ? "pr-3" : ""}
                             style={{height, width}}
                        >
                            <div
                                className="pr-3 "
                                style={{
                                    height: `${rowVirtualizer.totalSize}px`,
                                    width: `${width}px`,
                                    position: "relative"
                                }}
                            >
                                {rowVirtualizer.virtualItems.map(virtualRow => datasetImages.results[virtualRow.index] ? (
                                    <div
                                        key={virtualRow.index}
                                        className={`flex rounded justify-between items-center text-gray-600 transition-colors duration-150 ${(rowVirtualizer.totalSize + 45) > height ? 'pr-8' : 'pr-5'} pl-5 ${rowBG(virtualRow.index)}`}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: `${virtualRow.size}px`,
                                            transform: `translateY(${virtualRow.start}px)`
                                        }}
                                    >
                                        <div className="w-40 select-text">{datasetImages.results[virtualRow.index].dataset_id}</div>
                                        <div className="w-24 select-text">{datasetImages.results[virtualRow.index].sequence}</div>
                                        <div className="w-96 select-text truncate">{datasetImages.results[virtualRow.index].name}</div>
                                        <div className="w-36 whitespace-nowrap select-text">
                                         <span>{imageStatuses.find(x => x.id === datasetImages.results[virtualRow.index].status)?.name}</span>
                                        </div>
                                        <div className="w-32 text-right"><Button onClick={() => setCurrentImage(virtualRow.index)} size={"small"}>View</Button></div>

                                    </div>
                                ) : null)}
                            </div>
                        </div>
                    )}
                </AutoSizer>
            }
            </div>
        </div>
    )
}

export default ImagesList;
